import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardSchoolOverview.css';
import { supabase } from '../../lib/supabase';

const DashboardSchoolOverview = ({ dataLoading, studentsCount, teachersCount, gradesCount, subjectsCount, schoolData, refreshActivities }) => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showDeleteItemConfirm, setShowDeleteItemConfirm] = useState(false);

  // Fetch activities from database
  useEffect(() => {
    fetchActivities();
  }, []);

  // Refresh activities when refreshActivities prop changes
  useEffect(() => {
    if (refreshActivities) {
      fetchActivities();
    }
  }, [refreshActivities]);

  const fetchActivities = async () => {
    try {
      setActivitiesLoading(true);
      const schoolId = localStorage.getItem('school_id');

      if (!schoolId) return;

      // Fetch only the last 5 activities for dashboard preview
      const { data, error } = await supabase
        .from('cms_activities')
        .select('*')
        .eq('school_id', schoolId)
        .order('added_at', { ascending: false })
        .limit(5);

      if (error) {} else {
        setActivities(data || []);
      }
    } catch (err) {
      } finally {
      setActivitiesLoading(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      // Optimistically remove from UI
      setActivities(prev => prev.filter(activity => activity.id !== activityId));

      const { error } = await supabase
        .from('cms_activities')
        .delete()
        .eq('id', activityId);

      if (error) {
        await fetchActivities();
        alert('Error deleting activity: ' + error.message);
      } else {
        // Success - activities already updated optimistically
      }

      setShowDeleteItemConfirm(false);
      setDeleteItemId(null);
    } catch (err) {
      await fetchActivities();
      alert('Error deleting activity: ' + err.message);
      setShowDeleteItemConfirm(false);
      setDeleteItemId(null);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - activityTime) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const getActivityIcon = (title) => {
    if (title.toLowerCase().includes('student')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
        </svg>
      );
    } else if (title.toLowerCase().includes('teacher')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z" />
        </svg>
      );
    } else {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
        </svg>
      );
    }
  };

  return (
    <div className="school-overview-content">
      <div className="school-overview-stats-grid">
        <div className="school-overview-stat-card">
          <div className="school-overview-stat-info">
            <h3>Total Students</h3>
            <div className="school-overview-stat-number">{dataLoading ? '...' : studentsCount}</div>
          </div>
          <div className="school-overview-stat-icon school-overview-student-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
            </svg>
          </div>
        </div>

        <div className="school-overview-stat-card">
          <div className="school-overview-stat-info">
            <h3>Total Teachers</h3>
            <div className="school-overview-stat-number">{dataLoading ? '...' : teachersCount}</div>
          </div>
          <div className="school-overview-stat-icon school-overview-teacher-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z" />
            </svg>
          </div>
        </div>

        <div className="school-overview-stat-card">
          <div className="school-overview-stat-info">
            <h3>Total Grades</h3>
            <div className="school-overview-stat-number">{dataLoading ? '...' : gradesCount}</div>
          </div>
          <div className="school-overview-stat-icon school-overview-grades-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 3L5 7L9 11V8H13V6H9V3M11 13V16H7V18H11V21L15 17L11 13M19 3V7H21V9H17V7H19V3H17V1H21V3H19M19 21V17H17V15H21V17H19V21H21V23H17V21H19Z" />
            </svg>
          </div>
        </div>

        <div className="school-overview-stat-card">
          <div className="school-overview-stat-info">
            <h3>Total Subjects</h3>
            <div className="school-overview-stat-number">{dataLoading ? '...' : subjectsCount}</div>
          </div>
          <div className="school-overview-stat-icon school-overview-subjects-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" />
            </svg>
          </div>
        </div>

        <div className="school-overview-stat-card">
          <div className="school-overview-stat-info">
            <h3>School Name</h3>
            <div className="school-overview-stat-text">{dataLoading ? '...' : (schoolData?.name || 'Unknown School')}</div>
          </div>
          <div className="school-overview-stat-icon school-overview-school-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" />
            </svg>
          </div>
        </div>
      </div>


      <div className="school-overview-recent-activity">
        <div className="school-overview-activity-header">
          <h2>Recent Activity</h2>
          <button
            className="school-overview-view-all-btn"
            onClick={() => navigate('/mydashboard/activities')}
          >
            View All
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            </svg>
          </button>
        </div>

        {activitiesLoading ? (
          <div className="school-overview-activity-loading">
            <div className="school-overview-activity-spinner">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
              </svg>
            </div>
            <p>Loading activities...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="school-overview-no-activity">
            <div className="school-overview-no-activity-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <h3>No Recent Activity</h3>
            <p>Activities will appear here when you add students or teachers.</p>
          </div>
        ) : (
          <div className="school-overview-activity-list-container">
            <div className="school-overview-activity-list">
              {activities.map((activity) => (
                <div key={activity.id} className="school-overview-activity-item">
                  <div className="school-overview-activity-icon">
                    {getActivityIcon(activity.title)}
                  </div>
                  <div className="school-overview-activity-content">
                    <p><strong>{activity.title}</strong></p>
                    {activity.details && <p className="school-overview-activity-details">{activity.details}</p>}
                    <span className="school-overview-activity-time">{formatTimeAgo(activity.added_at)}</span>
                  </div>
                  <button
                    className="school-overview-activity-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteItemId(activity.id);
                      setShowDeleteItemConfirm(true);
                    }}
                    title="Delete activity"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>


      {/* Delete Single Activity Confirmation Modal */}
      {showDeleteItemConfirm && (
        <div className="school-overview-modal-overlay" onClick={() => {
          setShowDeleteItemConfirm(false);
          setDeleteItemId(null);
        }}>
          <div className="school-overview-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="school-overview-confirmation-header">
              <div className="school-overview-confirmation-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
              </div>
              <h2>Delete Activity</h2>
            </div>

            <div className="school-overview-confirmation-content">
              <p className="school-overview-confirmation-message">
                Are you sure you want to delete this activity?
              </p>
              <p className="school-overview-confirmation-warning">
                This action cannot be undone.
              </p>
            </div>

            <div className="school-overview-confirmation-actions">
              <button className="school-overview-cancel-btn" onClick={() => {
                setShowDeleteItemConfirm(false);
                setDeleteItemId(null);
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
                Cancel
              </button>
              <button className="school-overview-confirm-btn" onClick={() => handleDeleteActivity(deleteItemId)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardSchoolOverview;
